/**
 * Sprint 23.6 v2 — AgentDecisions (left column of the Decisions+Learnings band).
 *
 * The "watch her think" column (Q6). Shows the 2–3 most recent non-system
 * activities authored by the agent as decision cards:
 *
 *   • Subject  — the decision itself (the `subject` column).
 *   • Reasoning — the `details` column (Sprint 14e, free-form). When it's a
 *     JSON object, we extract a `reason` / `rationale` / `note` field; when
 *     it's a string, render directly. Fallback to the activity `summary`.
 *   • Footer — timestamp + "View full trace →" deeplink to the Activity tab.
 *
 * Empty state (Q22): a themed inline sentence (not a card) with a
 * "give them their first task" link that adds a queued task and jumps to the
 * Queue lens.
 *
 * Visual contract: card = background-secondary, border-radius-md, padding
 * 9px 11px; title 12/500; reasoning 11px secondary line-height 1.5; footer 10px
 * tertiary.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useActivitiesByActor } from "../../hooks/use-activities";
import type { Activity } from "../../../lib/types";

export interface AgentDecisionsProps {
  agentId: string;
  agentName: string;
  /** ISO of when the agent was created — kept for symmetry with the learnings column. */
  agentCreatedAt: string;
}

export function AgentDecisions({ agentId }: AgentDecisionsProps) {
  const navigate = useNavigate();
  const query = useActivitiesByActor("agent", agentId, 8);

  // Filter system noise — only user-facing decisions (Q6: channel != 'system').
  // Bug 1 (Sprint 27.0): exclude activities whose subject is archived.
  const decisions = useMemo(() => {
    const all = query.data ?? [];
    return all
      .filter((a) => (a.channel as string) !== "system" && !a.archived_at)
      .slice(0, 3);
  }, [query.data]);

  return (
    <div style={column}>
      <h3 style={header}>Decisions</h3>
      {query.isLoading ? (
        <div style={emptyText}>Loading…</div>
      ) : decisions.length === 0 ? (
        <p style={emptyText}>
          No decisions recorded yet. Decisions are captured during retrospectives.
        </p>
      ) : (
        <ul style={list}>
          {decisions.map((a) => {
            const reasoning = extractReasoning(a);
            return (
              <li key={a.id} style={card}>
                <div style={cardTitle}>{a.subject}</div>
                {reasoning && <div style={cardReason}>{reasoning}</div>}
                <button
                  type="button"
                  style={cardFooter}
                  onClick={() => navigate(`/agents/${agentId}#activity?highlight=${a.id}`)}
                >
                  {relativeTime(a.occurred_at)} · View full trace →
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function extractReasoning(a: Activity): string | null {
  if (typeof a.details === "string" && a.details.trim().length > 0) {
    return truncate(a.details, 200);
  }
  if (a.details && typeof a.details === "object") {
    const obj = a.details as Record<string, unknown>;
    for (const key of ["reason", "rationale", "note", "reasoning", "summary"]) {
      const value = obj[key];
      if (typeof value === "string" && value.trim().length > 0) return truncate(value, 200);
    }
  }
  if (a.summary && a.summary.trim().length > 0) return truncate(a.summary, 200);
  return null;
}

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const column: CSSProperties = {
  minWidth: 0,
};

const header: CSSProperties = {
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  margin: 0,
  padding: 0,
  listStyle: "none",
};

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "9px 11px",
};

const cardTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const cardReason: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
  marginTop: 2,
};

const cardFooter: CSSProperties = {
  display: "block",
  marginTop: 3,
  padding: 0,
  border: "none",
  background: "transparent",
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  textAlign: "left",
};

const emptyText: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
};

